import type { Color } from "@salestrak/ui";
import type { WidgetSeed } from "#app/widgets/dashboards/use-widget-view";
import type { Omittable } from "@salestrak/ui";

/**
 * The managed-surface contract, shared by every managed catalog (My Work, Team
 * Overview, …). It lives in this neutral module rather than any one surface's
 * catalog so surfaces can depend on the shape without importing each other.
 *
 * A managed widget is a `WidgetSeed` plus the metadata the Manage-widgets panel
 * and the host effects read: a stable `widgetKey` (matched back to a placed
 * instance via the key `managedSeed` bakes into `config`), display copy,
 * whether it seeds on a first empty visit, and whether it is `disabled` — out
 * of scope: listed greyed out with no toggle, never seeded, and retired from a
 * board that already placed it.
 */
export type ManagedWidget = WidgetSeed & Omittable<{
  widgetKey: string;
  label: string;
  description: string;
  color?: Color;
  defaultOn: boolean;
  disabled?: boolean;
}>;

/**
 * Bake a surface's managed-layout revision into a widget config. Each surface
 * defines ONE version const and threads it through this helper — never a
 * per-widget literal, since one missed literal on a bump makes that widget
 * re-migrate on every visit (D11).
 */
export function withManagedLayoutVersion(
  version: number,
  config: Record<string, unknown> = {},
): Record<string, unknown> {
  return { ...config, managedLayoutVersion: version };
}

/** Flatten a managed catalog entry into the `WidgetSeed` the store persists,
 *  re-baking `widgetKey` into config so the instance can be matched later. */
export function managedSeed(widget: ManagedWidget): WidgetSeed {
  return {
    type: widget.type,
    config: { ...widget.config, widgetKey: widget.widgetKey },
    x: widget.x,
    y: widget.y,
    w: widget.w,
    h: widget.h,
  };
}

/**
 * The configs of managed widgets the viewer hid, by `widgetKey`. Hide removes
 * the instance, so without this a Notepad's text (or any renamed or tuned
 * card) would come back blank on Show. Stored in the user-settings bag under
 * `hiddenManagedConfigKey(view)`, so a hidden card keeps its content across
 * reloads and devices.
 */
export type HiddenManagedConfigs = Record<string, Record<string, unknown>>;

export function hiddenManagedConfigKey(view: string): string {
  return `widget-view-hidden-configs:${view}`;
}

/** Loose JSON → the stash; anything that is not an object of objects is dropped. */
export function parseHiddenManagedConfigs(value: unknown): HiddenManagedConfigs {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: HiddenManagedConfigs = {};
  for (const [key, config] of Object.entries(value)) {
    if (config && typeof config === "object" && !Array.isArray(config)) {
      out[key] = config as Record<string, unknown>;
    }
  }
  return out;
}

/** The seed a Show places: the catalog seed, carrying the config the widget
 *  had when it was hidden (with the `widgetKey` re-baked), if one was kept. */
export function restoreManagedSeed(
  widget: ManagedWidget,
  hidden: HiddenManagedConfigs,
): WidgetSeed {
  const seed = managedSeed(widget);
  const kept = hidden[widget.widgetKey];
  return kept ? { ...seed, config: { ...kept, widgetKey: widget.widgetKey } } : seed;
}
